// CartComponent.js - Contains functions for creating cart elements
import { v4 as uuidv4 } from 'uuid';

/**
 * Creates a complete cart system with the exact same structure as in App.js
 * @param {string} builderId - The builder ID for the elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the cart is being added
 * @returns {Object} - The cart elements
 */
export const createCartElements = (builderId, getHierarchyName, currentPageId) => {
  const cartId = Date.now();
  const cartButtonId = Date.now()+1;
  const cartContainerId = Date.now()+22;
  const cartWrapperId = Date.now()+2;
  const cartTitleId = Date.now()+3;
  const cartContentId = Date.now()+4;
  const emptyStateId = Date.now()+5;
  const emptyCartMessageId = Date.now()+6;
  const shopNowBtnId = Date.now()+7;
  const cartTotalId = Date.now()+8;
  const cartTotalTextId = Date.now()+9;
  const checkoutBtnId = Date.now()+10;
  const closeButtonId = Date.now()+11;
  
  // Test product IDs
  const testProductId = Date.now()+12;
  const productImageId = Date.now()+13;
  const productInfoId = Date.now()+14;
  const productNameId = Date.now()+15;
  const productQuantityId = Date.now()+16;
  const minusBtnId = Date.now()+17;
  const quantityValueId = Date.now()+18;
  const plusBtnId = Date.now()+19;
  const productPriceId = Date.now()+20;
  const removeButtonId = Date.now()+21;
  
  const newElements = [
    // Cart Container (main)
    {
      id: cartId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cart',
      name: 'Cart',
      parentId: null,
      styles: {
        position: 'static'
      },
      className: '',
      children: [cartButtonId, cartContainerId],
      text: '',
      attributes: {
        'data-element': 'cart',
        'data-component': 'ecommerce',
        'data-cart-id': cartId,
        'data-page-id': currentPageId
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      cartPageId: currentPageId
    },
    // Add new Cart Container div
    {
      id: cartContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Cart Container',
      parentId: cartId,
      hierarchyName: "Cart Container",
      styles: {
        position: 'relative',
        display: 'none',
        zIndex: 1000
      },
      className: '',
      children: [cartWrapperId],
      text: '',
      attributes: {
        'data-element': 'cart-container'
      }
    },
    // Cart Button
    {
      id: cartButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cart-button',
      name: 'Cart Button',
      parentId: cartId,
      hierarchyName: "Cart Button",
      styles: {
        display: 'inline-block',
        // alignItems: 'center',
        // gap: '8px',
        padding: '8px 16px',
        backgroundColor: '#2a2a2a',
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      },
      className: '',
      children: [],
      text: 'Cart (0)',
      attributes: {
        'data-element': 'cart-button',
        'data-action': 'toggle-cart',
        'data-count': '0',
        'aria-label': 'Toggle shopping cart',
        'aria-expanded': 'false',
        'aria-haspopup': 'dialog',
        'aria-controls': cartWrapperId
      },
      interactions: {},
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
    },
    // Cart Wrapper
    {
      id: cartWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cart-wrapper',
      name: 'Cart Wrapper',
      parentId: cartContainerId,
      styles: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
        border: '1px solid #333333',
        color: '#ffffff',
        width: '400px',
        zIndex: 1000,
        position: 'absolute'
      },
      className: '',
      children: [cartTitleId, closeButtonId, cartContentId, cartTotalId, checkoutBtnId],
      text: '',
      attributes: {
        'data-element': 'cart-wrapper',
        'role': 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': cartTitleId,
        'hidden': 'true'
      }
    },
    // Cart Title
    {
      id: cartTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Cart Title',
      parentId: cartWrapperId,
      styles: {
        margin: '0 0 20px 0',
        fontSize: '20px',
        fontWeight: '600',
        color: '#ffffff'
      },
      className: '',
      children: [],
      text: 'Shopping Cart',
      attributes: {
        'data-element': 'cart-title',
        'id': cartTitleId
      }
    },
    // Close Button
    {
      id: closeButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Close Button',
      parentId: cartWrapperId,
      styles: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        width: '30px',
        height: '30px',
        backgroundColor: 'transparent',
        border: 'none',
        color: '#ffffff',
        fontSize: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        borderRadius: '50%'
      },
      className: '',
      children: [],
      text: '×',
      attributes: {
        'data-element': 'close-button',
        'data-action': 'close-cart',
        'aria-label': 'Close cart dialog'
      }
    },
    // Cart Content
    {
      id: cartContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cart-content',
      name: 'Cart Content',
      parentId: cartWrapperId,
      styles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        marginBottom: '20px'
      },
      className: '',
      children: [testProductId, emptyStateId],
      text: '',
      attributes: {
        'data-element': 'my-cart-content',
        'role': 'list',
        'aria-label': 'Shopping cart items'
      }
    },
    // Empty State Container
    {
      id: emptyStateId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Empty State',
      hierarchyName: "Empty State",
      parentId: cartContentId,
      styles: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 0',
        gap: '15px'
      },
      className: '',
      children: [emptyCartMessageId, shopNowBtnId],
      text: '',
      attributes: {
        'data-element': 'empty-state',
        'data-state': 'empty'
      }
    },
    // Test Product (hidden by default)
    {
      id: testProductId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Test Product',
      parentId: cartContentId,
      hierarchyName: "Test Product 1",
      styles: {
        display: 'none',
        alignItems: 'center',
        padding: '10px 0px',
        borderBottom: '1px solid #333333',
        gap: '15px',
        width: '100%'
      },
      className: '',
      children: [productImageId, productInfoId, productPriceId, removeButtonId],
      text: '',
      attributes: {
        'data-element': 'cart-item',
        'data-product-id': 'Test-Product-1',
        'data-price': '49.99',
        'data-quantity': '1',
        'role': 'listitem'
      }
    },
    // Product Image
    {
      id: productImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Product Image',
      parentId: testProductId,
      styles: {
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        borderRadius: '4px',
        backgroundColor: '#2a2a2a'
      },
      className: '',
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=300&fit=crop',
      attributes: {
        'data-element': 'product-image',
        'alt': 'Premium Wireless Headphones'
      }
    },
    // Product Info
    {
      id: productInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Info',
      parentId: testProductId,
      styles: {
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        gap: '5px'
      },
      className: '',
      children: [productNameId, productQuantityId],
      text: '',
      attributes: {
        'data-element': 'product-info'
      }
    },
    // Product Name
    {
      id: productNameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Product Name',
      parentId: productInfoId,
      styles: {
        margin: '0',
        fontSize: '16px',
        fontWeight: '500',
        color: '#ffffff'
      },
      className: '',
      children: [],
      text: 'Premium Wireless Headphones',
      attributes: {
        'data-element': 'product-name'
      }
    },
    // Product Quantity
    {
      id: productQuantityId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Product Quantity',
      parentId: productInfoId,
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: '#999999',
        fontSize: '14px'
      },
      className: '',
      children: [minusBtnId, quantityValueId, plusBtnId],
      text: '',
      attributes: {
        'data-element': 'product-quantity',
        'data-quantity': '1'
      }
    },
    // Minus Button
    {
      id: minusBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'quantity-minus',
      name: 'Minus Button',
      parentId: productQuantityId,
      styles: {
        width: '28px',
        height: '28px',
        backgroundColor: '#333333',
        border: 'none',
        borderRadius: '4px',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: 'bold'
      },
      className: '',
      children: [],
      text: '-',
      attributes: {
        'data-element': 'quantity-minus',
        'data-action': 'decrease-quantity',
        'data-product-id': 'sample-product-123',
        'aria-label': 'Decrease quantity'
      },
      states: {
        hover: {
          backgroundColor: '#444444'
        },
        pressed: {
          backgroundColor: '#222222',
          transform: 'scale(0.95)'
        }
      }
    },
    // Quantity Value
    {
      id: quantityValueId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Quantity Value',
      parentId: productQuantityId,
      styles: {
        display: 'inline-block',
        minWidth: '20px',
        textAlign: 'center',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: '500'
      },
      className: '',
      children: [],
      text: '1',
      attributes: {
        'data-element': 'quantity-value',
        'data-quantity': '1'
      }
    },
    // Plus Button
    {
      id: plusBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'quantity-plus',
      name: 'Plus Button',
      parentId: productQuantityId,
      styles: {
        width: '28px',
        height: '28px',
        backgroundColor: '#333333',
        border: 'none',
        borderRadius: '4px',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: 'bold'
      },
      className: '',
      children: [],
      text: '+',
      attributes: {
        'data-element': 'quantity-plus',
        'data-action': 'increase-quantity',
        'data-product-id': 'sample-product-123',
        'aria-label': 'Increase quantity'
      },
      states: {
        hover: {
          backgroundColor: '#444444'
        },
        pressed: {
          backgroundColor: '#222222',
          transform: 'scale(0.95)'
        }
      }
    },
    // Product Price
    {
      id: productPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Product Price',
      parentId: testProductId,
      styles: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff',
        minWidth: '80px',
        textAlign: 'right'
      },
      className: '',
      children: [],
      text: '$49.99', // Updated to show the product price
      attributes: {
        'data-element': 'product-price',
        'data-price': '49.99' // Updated to show the product price
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
    },
    // Remove Button
    {
      id: removeButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'remove-button',
      name: 'Remove Button',
      parentId: testProductId,
      styles: {
        backgroundColor: 'transparent',
        border: 'none',
        color: '#ff4444',
        cursor: 'pointer',
        padding: '5px',
        fontSize: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '30px'
      },
      className: '',
      children: [],
      text: '×',
      attributes: {
        'data-element': 'remove-button',
        'data-action': 'remove-item',
        'data-product-id': 'Test-Product-1',
        'aria-label': 'Remove item from cart'
      }
    },
    // Empty Cart Message (now inside Empty State div)
    {
      id: emptyCartMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Empty Cart Message',
      parentId: emptyStateId,
      styles: {
        margin: '0',
        textAlign: 'center',
        color: '#999999',
        fontSize: '14px',
        display: 'block' // Visible by default
      },
      className: '',
      children: [],
      text: 'Your cart is empty',
      attributes: {
        'data-element': 'empty-cart-message'
      }
    },
    // Shop Now Button (now inside Empty State div)
    {
      id: shopNowBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Shop Now Button',
      parentId: emptyStateId,
      styles: {
        padding: '10px 20px',
        backgroundColor: '#007bff',
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        width: 'fit-content',
        margin: '0 auto',
        display: 'block' // Visible by default
      },
      className: '',
      children: [],
      text: 'Start Shopping',
      attributes: {
        'data-element': 'shop-now-button',
        'data-action': 'start-shopping'
      },
      states: {
        hover: {
          backgroundColor: '#0056b3'
        }
      }
    },
    // Cart Total
    {
      id: cartTotalId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Cart Total',
      parentId: cartWrapperId,
      styles: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 0',
        borderTop: '1px solid #333333',
        marginBottom: '15px'
      },
      className: '',
      children: [cartTotalTextId],
      text: '',
      attributes: {
        'data-element': 'cart-total'
      }
    },
    // Cart Total Text
    {
      id: cartTotalTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Cart Total Text',
      parentId: cartTotalId,
      styles: {
        margin: '0',
        fontSize: '18px',
        fontWeight: '600',
        color: '#ffffff'
      },
      className: '',
      children: [],
      text: 'Total: $0.00', // Changed to $0.00 by default
      attributes: {
        'data-element': 'cart-total-text',
        'data-amount': '0.00' // Changed to 0.00 by default
      }
    },
    // Checkout Button
    {
      id: checkoutBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Checkout Button',
      parentId: cartWrapperId,
      styles: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#28a745',
        color: '#ffffff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500'
      },
      className: '',
      children: [],
      text: 'Proceed to Checkout',
      attributes: {
        'data-element': 'checkout-button',
        'data-action': 'checkout',
        'disabled': 'true' // Disabled by default
      },
      states: {
        hover: {
          backgroundColor: '#218838'
        },
        disabled: {
          backgroundColor: '#1e7e34',
          cursor: 'not-allowed',
          opacity: '0.7'
        }
      }
    }
  ];
  
  // Return all elements
  return {
    cartContainer: newElements[0],
    cartButton: newElements[1],
    cartContainerDiv: newElements[2],
    cartWrapper: newElements[3],
    elements: newElements
  };
};

export default createCartElements; 